/**
 * WHEN A SYMBOL-DEPENDENT COMMAND MAY RUN.
 *
 * <h2>The race this exists for</h2>
 *
 * Changing the symbol fires an effect that calls {@code clearOverlays()} — so an overlay added
 * in the same tick as {@code setSelectedSymbol} is wiped a moment later by the clear it had just
 * triggered. {@code App.js} records that at the `pendingScan` effect, which is the pattern this
 * generalises: queue the work, and run it only once the new bars are on the chart.
 *
 * <h2>⚠ `barData.symbol` IS THE AUTHORITY, NOT `selectedSymbol`</h2>
 *
 * The symbol state commits immediately and the clear effect fires off it; the bars arrive a
 * fetch later. So {@code selectedSymbol === cmd.symbol} means "the clear has happened and the
 * bars have not", which is the one moment a paint must NOT run. Only
 * {@code barSymbol === cmd.symbol} means both halves are done.
 *
 * <h2>Pure, because the interesting part is the classification</h2>
 *
 * Four outcomes and the rules between them are the thing most likely to be wrong, and they
 * cannot be exercised through a component that needs auth, a chart and a dozen contexts to
 * mount. The effect in {@code App.js} is left with the side effects alone.
 */

/** What should happen to a queued command on this render. */
export const DISPOSITION = {
  /** Its bars are on the chart and the clear has already happened. Run it. */
  READY: 'READY',
  /** The fetch for its symbol failed. Reject it rather than leave it waiting. */
  FAILED: 'FAILED',
  /** Something moved the chart elsewhere after its symbol had been applied. */
  SUPERSEDED: 'SUPERSEDED',
  /** Nothing has happened yet. Leave it queued. */
  WAITING: 'WAITING',
};

/**
 * Decide what to do with one queued command.
 *
 * @param {object} cmd      `{ symbol, sawSymbol }` — `sawSymbol` records that this command's
 *                          symbol has been the selected one at least once, which is what lets a
 *                          later move away from it count as superseded rather than as
 *                          "not applied yet". The caller sets it; this function only reads it.
 * @param {object} state    `{ selectedSymbol, barSymbol, loading, error }`
 * @returns {{disposition: string, reason?: string}}
 */
export function dispositionOf(cmd, state) {
  const { selectedSymbol, barSymbol, loading, error } = state || {};
  const symbol = cmd && cmd.symbol;

  if (selectedSymbol === symbol) {
    if (barSymbol === symbol) return { disposition: DISPOSITION.READY };
    // ⚠ `loading` is checked BEFORE `error`, and the order is load-bearing. A stale error from
    // the PREVIOUS symbol's fetch is still set while this one is in flight, so reading error
    // alone rejects a command whose load is going perfectly well.
    if (!loading && error) {
      return { disposition: DISPOSITION.FAILED, reason: String(error) };
    }
    return { disposition: DISPOSITION.WAITING };
  }

  if (cmd && cmd.sawSymbol) {
    return {
      disposition: DISPOSITION.SUPERSEDED,
      reason: `the chart moved to ${selectedSymbol} before ${symbol} finished loading`,
    };
  }

  // The symbol change has not committed yet. Normal on the render that queued the command.
  return { disposition: DISPOSITION.WAITING };
}

/**
 * Split a queue into the commands to settle now and the ones still waiting.
 *
 * <p>Returns the ORIGINAL array object when nothing is ready, so a caller can skip the state
 * write entirely — otherwise every unrelated render of the host component would queue another
 * render with an identical array.
 *
 * @returns {{settle: Array<{cmd, disposition, reason}>, waiting: Array, changed: boolean}}
 */
export function partitionPendingCommands(pending, state) {
  const queue = Array.isArray(pending) ? pending : [];
  const settle = [];
  const waiting = [];

  queue.forEach((cmd) => {
    const { disposition, reason } = dispositionOf(cmd, state);
    if (disposition === DISPOSITION.WAITING) waiting.push(cmd);
    else settle.push({ cmd, disposition, reason });
  });

  return { settle, waiting: settle.length === 0 ? queue : waiting, changed: settle.length > 0 };
}
